import { Actions, ActionTypes } from '@appStore/metering-points/actions';
import { MeteringPointTabType } from '@enums';
import { ClimateCompensatedCO2Data, MeteringPoint, MeteringPointFilterSettings, MeteringPointSettings } from '@model';
import { DisruptionInfo } from 'app/models/disruptionInfo.model';

export interface State {
  loaded: boolean;
  loading: boolean;
  data: MeteringPoint[];
  key: string;
  keyLoaded: boolean;
  keyLoading: boolean;
  disruptionLoaded: boolean;
  disruptionLoading: boolean;
  selected: MeteringPoint;
  selectedMeteringPointTabIndex: MeteringPointTabType;
  selectedMeteringPointsTabIndex: number;
  selectedPageIndexHot: number;
  selectedPageIndexCold: number;
  selectedPageSizeHot: number;
  selectedPageSizeCold: number;
  sortColumnHot: string;
  sortColumnCold: string;
  sortDirectionHot: string;
  sortDirectionCold: string;
  scrollPosition: [number, number];
  climateCompensatedData: ClimateCompensatedCO2Data;
  climateCompensatedDataLoaded: boolean;
  climateCompensatedDataLoading: boolean;
  meteringPointSettings: MeteringPointSettings;
  /*
  hasIntelligy: boolean;
  intelligyReadyLoaded: boolean;
  intelligyReadyLoading: boolean;
  */
}

export const initialState: State = {
  loaded: false,
  loading: false,
  data: [],
  key: '',
  keyLoaded: false,
  keyLoading: false,
  disruptionLoaded: false,
  disruptionLoading: false,
  selected: null,
  selectedMeteringPointTabIndex: MeteringPointTabType.Details,
  selectedMeteringPointsTabIndex: 0,
  selectedPageIndexHot: 0,
  selectedPageIndexCold: 0,
  selectedPageSizeHot: 25,
  selectedPageSizeCold: 25,
  sortColumnHot: 'meteringPointId',
  sortColumnCold: 'meteringPointId',
  sortDirectionHot: 'desc',
  sortDirectionCold: 'desc',
  scrollPosition: [0, 0],
  climateCompensatedData: null,
  climateCompensatedDataLoaded: false,
  climateCompensatedDataLoading: false,
  meteringPointSettings: null,
  /*
  hasIntelligy: false,
  intelligyReadyLoaded: false,
  intelligyReadyLoading: false,
  */
};

export function reducer(state = initialState, action: Actions): State {
  let newMpState: MeteringPoint[];
  let selectedMp: MeteringPoint;

  switch (action.type) {
    case ActionTypes.LoadMeteringPointData:
      return { ...state, loaded: false, loading: true };

    case ActionTypes.LoadMeteringPointDataSuccess:
      return { ...state, loaded: true, loading: false, data: action.payload };

    case ActionTypes.LoadMeteringPointDataFail:
      return { ...state, loaded: false, loading: false };

    case ActionTypes.LoadMeteringPointsDisruptionsData:
      return { ...state, disruptionLoaded: false, disruptionLoading: true };

    case ActionTypes.LoadMeteringPointsDisruptionsDataSuccess:
      let disruptions = action.payload;
      newMpState = [];
      state.data.forEach(mp => {
        let m = disruptions.find(x => mp.id === x.meteringPointId);
        if (m) {
          let copy = [...m.disruptions];
          let orderedDisruptions = copy.sort((a, b) => { 
            let aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
            let bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
            // sort by startTime descending
            return bTime - aTime;
          });

          let filteredDisruptions = new Array<DisruptionInfo>();

          let latestDisruption = orderedDisruptions.find(d => !d.isRestored);
          if (latestDisruption){
            filteredDisruptions = [...filteredDisruptions, latestDisruption];
          }
          
          let lastRestored = orderedDisruptions.find(d => d.isRestored);
          if (lastRestored) {
            filteredDisruptions = [...filteredDisruptions, lastRestored];
          }
          newMpState.push({ ...mp, disruptionInfos: filteredDisruptions });
        } else {
          newMpState.push({ ...mp, disruptionInfos: null });
        }
      });
      return { ...state, disruptionLoaded: true, disruptionLoading: false, data: newMpState };

    case ActionTypes.LoadMeteringPointReadingDataFail:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, readings: null, readingsLoaded: false, readingsLoading: false });
          } else {
            newMpState.push({
              ...mp,
              readings: null,
              readingsLoaded: false,
              readingsLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, readings: null, readingsLoaded: false, readingsLoading: false }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.meteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.meteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp };

    case ActionTypes.LoadMeteringPointReadingData:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, readings: null, readingsLoaded: false, readingsLoading: true });
          } else {
            newMpState.push({
              ...mp,
              readings: null,
              readingsLoaded: false,
              readingsLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, readings: null, readingsLoaded: false, readingsLoading: true }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp };

    case ActionTypes.LoadMeteringPointReadingDataSuccess:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, readings: action.payload.data, readingsLoaded: true, readingsLoading: false });
          } else {
            newMpState.push({
              ...mp,
              readings: null,
              readingsLoaded: false,
              readingsLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, readings: action.payload.data, readingsLoaded: true, readingsLoading: false }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp };


    case ActionTypes.LoadMeteringPointFunctionControls:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          // Same metering point
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, functionControls: null, functionControlsLoaded: false, functionControlsLoading: true });
          } else {
            // Contained metering point
            newMpState.push({
              ...mp,
              functionControls: null,
              functionControlsLoaded: false,
              functionControlsLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, functionControls: null, functionControlsLoaded: false, functionControlsLoading: true }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp, keyLoaded: false, keyLoading: false, key: null };

    case ActionTypes.LoadMeteringPointFunctionControlsSuccess:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          // Same metering point
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, functionControls: action.payload.data, functionControlsLoaded: true, functionControlsLoading: false });
          } else {
            // Contained metering point
            newMpState.push({
              ...mp,
              functionControls: null,
              functionControlsLoaded: false,
              functionControlsLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, functionControls: action.payload.data, functionControlsLoaded: true, functionControlsLoading: false }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp, keyLoaded: false, keyLoading: false, key: null};

    case ActionTypes.LoadMeteringPointFunctionControlContract:
        newMpState = [];
        state.data.forEach(mp => {
          if (mp.id === action.payload.masterMeteringPointId) {
            // Same metering point
            if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
              newMpState.push({ ...mp, functionControlContract: null, functionControlContractLoaded: false, functionControlContractLoading: true });
            } else {
              // Contained metering point
              newMpState.push({
                ...mp,
                functionControlContract: null,
                functionControlContractLoaded: false,
                functionControlContractLoading: false,
                containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                  m.id === action.payload.meteringPointId
                    ? { ...m, functionControlContract: null, functionControlContractLoaded: false, functionControlContractLoading: true }
                    : m
                )
              });
            }
          } else {
            newMpState.push(mp);
          }
        });
        selectedMp = state.selected;
        if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
          selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
        }
        return { ...state, data: newMpState, selected: selectedMp, keyLoaded: false, keyLoading: false, key: null };

    case ActionTypes.LoadMeteringPointFunctionControlContractSuccess:
      newMpState = [];
      state.data.forEach(mp => {
        if (mp.id === action.payload.masterMeteringPointId) {
          // Same metering point
          if (action.payload.masterMeteringPointId === action.payload.meteringPointId) {
            newMpState.push({ ...mp, functionControlContract: action.payload.data, functionControlContractLoaded: true, functionControlContractLoading: false });
          } else {
            // Contained metering point
            newMpState.push({
              ...mp,
              functionControlContract: null,
              functionControlContractLoaded: false,
              functionControlContractLoading: false,
              containedMeteringPoints: mp.containedMeteringPoints.map(m =>
                m.id === action.payload.meteringPointId
                  ? { ...m, functionControlContract: action.payload.data, functionControlContractLoaded: true, functionControlContractLoading: false }
                  : m
              )
            });
          }
        } else {
          newMpState.push(mp);
        }
      });
      selectedMp = state.selected;
      if (state.selected && state.selected.id === action.payload.masterMeteringPointId) {
        selectedMp = newMpState.find(mp => mp.id === action.payload.masterMeteringPointId);
      }
      return { ...state, data: newMpState, selected: selectedMp, keyLoaded: false, keyLoading: false, key: null };

    case ActionTypes.CreateReportKey:
          return { ...state, keyLoaded: false, keyLoading: true, key: null };
    case ActionTypes.CreateReportKeyFail:
          return { ...state, keyLoaded: false, keyLoading: false };
    case ActionTypes.CreateReportKeySuccess:
          return { ...state, keyLoaded: true, keyLoading: false, key: action.payload };
    case ActionTypes.ClearMeteringPoints:
      return { ...state, loaded: false };
    case ActionTypes.SelectMeteringPoint:
      return { ...state, selected: action.payload };
    case ActionTypes.SelectMeteringPointById:
      let mp = state.data.find(x => x.id === action.payload);
      return { ...state, selected: mp };
    case ActionTypes.UnselectMeteringPoint:
      return { ...state, selected: null };
    case ActionTypes.SelectTabIndexMeteringPoint:
      return { ...state, selectedMeteringPointTabIndex: action.payload };
    case ActionTypes.SelectTabIndexMeteringPoints:
      return { ...state, selectedMeteringPointsTabIndex: action.payload };
    case ActionTypes.SelectPageIndexHotMeteringPoint:
      return {
        ...state,
        selectedPageIndexHot: action.pageIndex,
        selectedPageSizeHot: action.pageSize,
        sortColumnHot: action.sortColumn,
        sortDirectionHot: action.sortDirection,
        scrollPosition: action.scrollPosition
      };
    case ActionTypes.SelectPageIndexColdMeteringPoint:
      return {
        ...state,
        selectedPageIndexCold: action.pageIndex,
        selectedPageSizeCold: action.pageSize,
        sortColumnCold: action.sortColumn,
        sortDirectionCold: action.sortDirection,
        scrollPosition: action.scrollPosition
      };
    case ActionTypes.ClearScrollPosition:
      return { ...state, scrollPosition: [0, 0] };

    case ActionTypes.LoadClimateCompensatedCO2Data:
      return { ...state, climateCompensatedDataLoaded: false, climateCompensatedDataLoading: true };
  
    case ActionTypes.LoadClimateCompensatedCO2DataSuccess:
      return { ...state,
          climateCompensatedDataLoaded: true,
          climateCompensatedDataLoading: false,
          climateCompensatedData: action.payload };
  
    case ActionTypes.LoadClimateCompensatedCO2DataFail:
      return { ...state, climateCompensatedDataLoaded: false, climateCompensatedDataLoading: false };

    case ActionTypes.ClearClimateCompensatedCO2State:
      return {
            ...state,
            climateCompensatedData: null,
            climateCompensatedDataLoaded: false,
            climateCompensatedDataLoading: false
          };
    case ActionTypes.MeteringPointsFilterSaveData:
      return { ...state, meteringPointSettings: action.payload };  
  }
  return state;
}

export const getAllMeteringPoints = (state: State) => state.data;
export const getAllMeteringPointsAndLoaded = (state: State) => ({ meteringPoints: state.data, loaded: state.loaded });
export const getMeteringPointsLoaded = (state: State) => state.loaded;
export const getMeteringPointsLoading = (state: State) => state.loading;
export const getReportKey = (state: State) => state.key && state.keyLoaded;
export const getMeteringPointsFunctionControl = (state: State) => state.data;
export const getMeteringPointFunctionControlContract = (state: State) => state.data;
export const getMeteringPointDisruptionsLoaded = (state: State) => state.disruptionLoaded && state.loaded;

export const getMeteringPointHasIntelligy = (meteringPointId: number) => (state: State) => {
  return state.data.some(mp => mp.id == meteringPointId && mp.hasIntelligy)
};
export const getIsAnyMeteringPointUsingIntelligy = (state: State) => {
  let containedMeteringPoints = state.data.filter(mp => mp.containedMeteringPoints && mp.containedMeteringPoints.length > 0);

  return containedMeteringPoints.some(mp => mp.hasIntelligy) || state.data.some(mp => mp.hasIntelligy);
}
export const getSelectedMeteringPoint = (state: State) => state.selected;
export const getSelectedMeteringPointTabIndex = (state: State) => state.selectedMeteringPointTabIndex;
export const getSelectedMeteringPointsTabIndex = (state: State) => state.selectedMeteringPointsTabIndex;
export const getClimateCompensatedData = (state: State) => state.climateCompensatedData;
export const getClimateCompensatedDataLoaded = (state: State) => state.climateCompensatedDataLoaded;

export const getSelectedPagingPropertiesHot = (state: State) => {
  return {
    pageIndex: state.selectedPageIndexHot,
    pageSize: state.selectedPageSizeHot,
    sortColumn: state.sortColumnHot,
    sortDirection: state.sortDirectionHot,
    scrollPosition: state.scrollPosition
  };
};
export const getSelectedPagingPropertiesCold = (state: State) => {
  return {
    pageIndex: state.selectedPageIndexCold,
    pageSize: state.selectedPageSizeCold,
    sortColumn: state.sortColumnCold,
    sortDirection: state.sortDirectionCold,
    scrollPosition: state.scrollPosition
  };
};
export const getMeteringPointSettings = (state: State) => state.meteringPointSettings;